// =============================================================================
// api/floors.ts
// =============================================================================

import type { ReorderItem, UpdateFloorPayload, VFloor } from "@/types";
import { supabase } from "@cl/supabase";
import { handleSupabaseError } from "./_error";

//  ---------------
// FETCHS
//  ---------------
export const fetchFloorsByTour = async (tourId: string): Promise<VFloor[]> => {
  const { data, error } = await supabase
    .from("v_floors")
    .select("*")
    .eq("tour_id", tourId)
    .order("sort_order", { ascending: true });
  if (error) handleSupabaseError(error, "fetchFloorsByTour");
  return (data ?? []) as VFloor[];
};

export const fetchFloorView = async (floorId: string): Promise<VFloor | null> => {
  const { data, error } = await supabase
    .from("v_floors")
    .select("*")
    .eq("id", floorId)
    .single();
  if (error) return null;
  return data as VFloor;
};

//  ---------------
// CREATE
//  ---------------
export const createFloor = async (
  tourId: string,
  name: string,
  levelType: string,
  sort_order: number = 0,
): Promise<VFloor> => {
  const { count } = await supabase
    .from("floors")
    .select("*", { count: "exact", head: true })
    .eq("tour_id", tourId);

  const { data, error } = await supabase
    .from("floors")
    .insert([
      {
        tour_id: tourId,
        name,
        level_type: levelType,
        sort_order,
        is_main: count === 0,
      },
    ])
    .select("*")
    .single();

  if (error) handleSupabaseError(error, "createFloor");
  return data as VFloor;
};

export const createDefaultFloor = async (tourId: string): Promise<VFloor> => {
  const { data, error } = await supabase
    .from("floors")
    .insert([{ tour_id: tourId, is_main: true, sort_order: 0 }])
    .select("*")
    .single();
  if (error) handleSupabaseError(error, "createDefaultFloor");
  return { ...(data as VFloor), img_url: null };
};

//  ---------------
// UPDATE
//  ---------------
export const updateFloor = async (
  id: string,
  updates: UpdateFloorPayload,
): Promise<void> => {
  const { error } = await supabase.from("floors").update(updates).eq("id", id);
  if (error) handleSupabaseError(error, "updateFloor");
};

//  ---------------
// DELETE
//  ---------------
export const deleteFloor = async (id: string): Promise<void> => {
  const { error } = await supabase.from("floors").delete().eq("id", id);
  if (error) handleSupabaseError(error, "deleteFloor");
};

//  ---------------
// REORDER
//  ---------------
export const reorderFloors = async (floors: ReorderItem[]): Promise<void> => {
  const { error } = await supabase.rpc("update_floor_orders", {
    payload: floors,
  });
  if (error) handleSupabaseError(error, "reorderFloors");
};

//  ---------------
// SET MAIN
//  ---------------
export const setMainFloor = async (floorId: string): Promise<void> => {
  const { error } = await supabase.rpc("set_main_floor", {
    p_floor_id: floorId,
  });
  if (error) handleSupabaseError(error, "setMainFloor");
};